/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * ProductForm — edicao de produto por perfil (ADR-0047 F2b). Mirror do SupplierForm.
 * Dois grupos: Identidade (ERP) e Logistica (WMS). Cada campo fica disabled conforme
 * product.editable_fields (vindo do backend, self-describing — ZERO hardcoding do split).
 * Sem "Novo produto" (D3: o WMS nao cria, so' edita). PATCH no guardar (backend filtra).
 * ========================================================================== */
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/lib/api'
import ProductImagesDialog from './components/ProductImagesDialog'

type ProductDTO = {
  id?: number
  code?: string
  variant?: string
  name1?: string
  name2?: string
  uom?: number | null
  is_active?: boolean
  storage_class?: string
  pick_mode?: string
  // ADR-0055 CORRECAO-3 (seccao Controlos): o gate "tem validade", o controlo de
  // lote (NAO batch_control) e a MARGEM. A VERDADE e' o backend (_apply_control_chain);
  // a UI so' reflecte a derivacao.
  expiry_controlled?: boolean
  lot_controlled?: boolean
  min_shelf_life_days?: number | string | null
  net_weight_kg?: string | number | null
  gross_weight_kg?: string | number | null
  height_mm?: string | number | null
  length_mm?: string | number | null
  width_mm?: string | number | null
  editable_fields?: string[]
}
type Uom = { id: number; code: string; name: string }
// contrato de shelf-life do produto (via /api/partners/shelf-life-contracts/?product=<id>).
// So' precisamos do minimo para a contagem "ativos" e o link para a ficha da contraparte.
type ContractDTO = {
  id: number
  party_type: 'CUSTOMER' | 'SUPPLIER'
  customer?: number | null
  supplier?: number | null
  action_block_lot?: boolean
}

const WMS_NUMS = [
  'net_weight_kg',
  'gross_weight_kg',
  'height_mm',
  'length_mm',
  'width_mm',
] as const

// step do input por-campo = decimal_places reais (pesos=3, dimensoes=2). Sem step fixo
// a 0.001 nas dimensoes (que sao 2 casas) -> o backend (DecimalField) rejeitaria 3 decimais.
const STEP: Record<string, string> = {
  net_weight_kg: '0.001',
  gross_weight_kg: '0.001',
  height_mm: '0.01',
  length_mm: '0.01',
  width_mm: '0.01',
}

/** Le o corpo do erro DRF e separa erros-de-campo de erros gerais. Trata os 3 casos:
 *  {campo: [msg]} (campos), {detail: "..."} e {non_field_errors: [...]} (gerais). */
function parseDrfError(data: any): {
  fields: Record<string, string>
  form: string
} {
  if (!data || typeof data !== 'object') {
    return { fields: {}, form: String(data ?? 'Erro ao guardar.') }
  }
  if (typeof data.detail === 'string') return { fields: {}, form: data.detail }
  const fields: Record<string, string> = {}
  const lines: string[] = []
  for (const [k, v] of Object.entries(data)) {
    const msg = Array.isArray(v) ? v.join(' ') : String(v)
    if (k === 'non_field_errors') lines.push(msg)
    else fields[k] = msg
  }
  return { fields, form: lines.join(' · ') }
}

export default function ProductForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [form, setForm] = useState<ProductDTO>({})
  const [editable, setEditable] = useState<string[]>([])
  const [imgOpen, setImgOpen] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')

  const { data, isFetching } = useQuery({
    queryKey: ['product', id],
    enabled: !!id,
    queryFn: async () =>
      (await api.get(`/api/product/products/${id}/`)).data as ProductDTO,
  })

  // dropdown de UOM (FK; so' relevante quando o uom e' editavel)
  const { data: uoms } = useQuery({
    queryKey: ['uoms'],
    queryFn: async () => {
      const d = (
        await api.get('/api/product/uoms/', { params: { page_size: 1000 } })
      ).data
      return (Array.isArray(d) ? d : (d?.results ?? [])) as Uom[]
    },
  })

  // contratos de shelf-life DESTE produto (read-only informativo). A "presenca de
  // contrato ativo" (action_block_lot=true) e' o que deriva expiry_controlled no backend.
  const { data: contracts } = useQuery({
    queryKey: ['product-shelf-contracts', id],
    enabled: !!id,
    queryFn: async () => {
      const d = (
        await api.get('/api/partners/shelf-life-contracts/', {
          params: { product: id, page_size: 1000 },
        })
      ).data
      return (Array.isArray(d) ? d : (d?.results ?? [])) as ContractDTO[]
    },
  })

  useEffect(() => {
    if (data) {
      setForm(data)
      setEditable(data.editable_fields ?? [])
    }
  }, [data])

  const save = useMutation({
    mutationFn: async () => {
      // PATCH so' com os campos editaveis (a lista que o backend mandou). ''->null nos
      // numericos (DecimalField rejeita '' -> 400; null=True aceita). Nao envia ERP (filtrado
      // na F2a de qualquer forma), nem editable_fields/images/id. Espelha a muralha no cliente.
      // Tres ramos por tipo do campo:
      //  - numerico (WMS_NUMS): vazio/null -> null (DecimalField null=True).
      //  - texto (CharField): null/undefined -> '' (CharField sem null=True rejeita null).
      //  - FK/bool (NON_TEXT): verbatim (uom/company = FK int; is_active = bool).
      // bool/FK enviados verbatim (inclui os controlos expiry/lot). min_shelf_life_days
      // e' inteiro nulavel -> mesma coercao ''->null dos WMS_NUMS (mas NAO entra na
      // grelha de dimensoes; por isso um conjunto proprio, nao WMS_NUMS).
      const NON_TEXT = new Set([
        'uom',
        'company',
        'is_active',
        'expiry_controlled',
        'lot_controlled',
      ])
      const NUM_NULLABLE = new Set([...WMS_NUMS, 'min_shelf_life_days'])
      const payload: Record<string, any> = {}
      for (const name of editable) {
        let v = (form as any)[name]
        if (NUM_NULLABLE.has(name)) {
          if (v === '' || v === undefined || v === null) v = null
        } else if (!NON_TEXT.has(name)) {
          if (v === null || v === undefined) v = ''
        }
        payload[name] = v
      }
      const { data: out } = await api.patch(
        `/api/product/products/${id}/`,
        payload,
      )
      return out
    },
    onMutate: () => {
      // limpa os erros no inicio de cada submit
      setFieldErrors({})
      setFormError('')
    },
    onError: (e: any) => {
      const { fields, form } = parseDrfError(e?.response?.data)
      setFieldErrors(fields)
      setFormError(form)
    },
    onSuccess: () => navigate('/catalog/products'),
  })

  const onChange = (k: keyof ProductDTO, v: any) =>
    setForm((s) => ({ ...s, [k]: v }))

  // Derivacao VISIVEL (espelha _apply_control_chain do backend; UX, nao regra de negocio):
  //  - ligar validade -> forca lote (marcado+bloqueado) e revela a margem.
  //  - desligar validade -> lote volta a editavel (MANTEM o valor); margem esconde-se+limpa.
  const onToggleExpiry = (checked: boolean) =>
    setForm((s) => ({
      ...s,
      expiry_controlled: checked,
      lot_controlled: checked ? true : s.lot_controlled,
      min_shelf_life_days: checked ? s.min_shelf_life_days : null,
    }))

  const can = (name: string) => editable.includes(name) // backend manda; nao hardcodamos o split
  const busy = isFetching || save.isPending

  // Contratos ativos (action_block_lot) + link para a ficha da contraparte onde se gerem.
  // Dedup por contraparte (o mesmo cliente pode ter varias politicas p/ o mesmo SKU).
  const activeContracts = (contracts ?? []).filter((c) => c.action_block_lot)
  const partyLinks = Array.from(
    new Map(
      activeContracts.map((c) => {
        const isCust = c.party_type === 'CUSTOMER'
        const pid = isCust ? c.customer : c.supplier
        return [
          `${c.party_type}:${pid}`,
          {
            to: isCust
              ? `/partners/customers/${pid}/shelf-life-contracts`
              : `/partners/suppliers/${pid}/shelf-life-contracts`,
            label: isCust ? `Cliente #${pid}` : `Fornecedor #${pid}`,
          },
        ] as const
      }),
    ).values(),
  )
  // erros de campos que NAO sao mostrados inline (os WMS_NUMS tem span proprio) -> ao fundo
  const otherFieldErrors = Object.entries(fieldErrors).filter(
    ([k]) => !(WMS_NUMS as readonly string[]).includes(k),
  )

  return (
    <div className="grid gap-4">
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Editar produto {form.code ? `— ${form.code}` : ''}
          </h2>
          <div className="flex gap-2">
            <button
              className="btn subtle"
              onClick={() => navigate('/catalog/products')}
            >
              Cancelar
            </button>
            <button
              className="btn"
              onClick={() => save.mutate()}
              disabled={busy || editable.length === 0}
            >
              {save.isPending ? 'A guardar…' : 'Guardar'}
            </button>
          </div>
        </div>

        {/* Identidade (ERP) — bloqueada para nao-superuser (campos fora de editable_fields) */}
        <div className="mt-4">
          <h3 className="text-sm font-semibold opacity-80">Identidade (ERP)</h3>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="grid gap-1">
              <span className="text-sm">Código</span>
              <input
                className="input"
                value={form.code ?? ''}
                onChange={(e) => onChange('code', e.target.value)}
                disabled={busy || !can('code')}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Variante</span>
              <input
                className="input"
                value={form.variant ?? ''}
                onChange={(e) => onChange('variant', e.target.value)}
                disabled={busy || !can('variant')}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">UM</span>
              <select
                className="input"
                value={form.uom ?? ''}
                onChange={(e) =>
                  onChange('uom', Number(e.target.value) || null)
                }
                disabled={busy || !can('uom')}
              >
                <option value="">—</option>
                {(uoms ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.code} — {u.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 md:col-span-2">
              <span className="text-sm">Nome</span>
              <input
                className="input"
                value={form.name1 ?? ''}
                onChange={(e) => onChange('name1', e.target.value)}
                disabled={busy || !can('name1')}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Nome (2)</span>
              <input
                className="input"
                value={form.name2 ?? ''}
                onChange={(e) => onChange('name2', e.target.value)}
                disabled={busy || !can('name2')}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Ativo</span>
              <select
                className="input"
                value={form.is_active ? '1' : '0'}
                onChange={(e) => onChange('is_active', e.target.value === '1')}
                disabled={busy || !can('is_active')}
              >
                <option value="1">Sim</option>
                <option value="0">Não</option>
              </select>
            </label>
          </div>
        </div>

        {/* Logistica (WMS) — editavel pelo armazem */}
        <div className="mt-5">
          <h3 className="text-sm font-semibold opacity-80">Logística (WMS)</h3>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="grid gap-1">
              <span className="text-sm">Classe de armazenagem</span>
              <input
                className="input"
                value={form.storage_class ?? ''}
                onChange={(e) => onChange('storage_class', e.target.value)}
                disabled={busy || !can('storage_class')}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Modo de picking</span>
              <select
                className="input"
                value={form.pick_mode ?? 'QUANTITY'}
                onChange={(e) => onChange('pick_mode', e.target.value)}
                disabled={busy || !can('pick_mode')}
              >
                <option value="QUANTITY">Quantidade</option>
                <option value="UNIT">Unidade</option>
              </select>
            </label>
            <div />
            {WMS_NUMS.map((name) => (
              <label key={name} className="grid gap-1">
                <span className="text-sm">{name}</span>
                <input
                  className="input"
                  type="number"
                  step={STEP[name] ?? '0.01'}
                  value={(form[name] as any) ?? ''}
                  onChange={(e) => onChange(name, e.target.value)}
                  disabled={busy || !can(name)}
                />
                {fieldErrors[name] && (
                  <span className="text-xs text-red-600">
                    {fieldErrors[name]}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Controlos (ADR-0055 CORRECAO-3) — validade/lote/margem + contratos.
            A derivacao e' visivel-bloqueada; a VERDADE e' o backend (_apply_control_chain). */}
        <div className="mt-5">
          <h3 className="text-sm font-semibold opacity-80">Controlos</h3>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
            {/* Controlo de validade — o gate "tem validade" (expiry_controlled) */}
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!!form.expiry_controlled}
                onChange={(e) => onToggleExpiry(e.target.checked)}
                disabled={busy || !can('expiry_controlled')}
              />
              <span className="grid gap-0.5">
                <span className="text-sm">Controlo de validade</span>
                <span className="text-xs opacity-70">
                  O produto tem prazo de validade; exige controlo de lote.
                </span>
              </span>
            </label>

            {/* Controlo de lote — derivacao VISIVEL-BLOQUEADA quando ha validade */}
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={!!form.lot_controlled}
                onChange={(e) => onChange('lot_controlled', e.target.checked)}
                disabled={
                  busy || !can('lot_controlled') || !!form.expiry_controlled
                }
              />
              <span className="grid gap-0.5">
                <span className="text-sm">Controlo de lote</span>
                {form.expiry_controlled ? (
                  <span className="text-xs font-medium text-amber-600">
                    Exigido por controlo de validade
                  </span>
                ) : (
                  <span className="text-xs opacity-70">
                    Rastreabilidade por lote (pode existir sem validade).
                  </span>
                )}
              </span>
            </label>

            {/* Margem minima — so' quando ha controlo de validade */}
            {form.expiry_controlled && (
              <label className="grid gap-1">
                <span className="text-sm">
                  Margem mínima de shelf-life (dias)
                </span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="1"
                  value={(form.min_shelf_life_days as any) ?? ''}
                  onChange={(e) =>
                    onChange('min_shelf_life_days', e.target.value)
                  }
                  disabled={busy || !can('min_shelf_life_days')}
                />
                <span className="text-xs opacity-70">
                  Vazio = sem margem própria (cai no piso do armazém, depois 0).
                </span>
                {fieldErrors['min_shelf_life_days'] && (
                  <span className="text-xs text-red-600">
                    {fieldErrors['min_shelf_life_days']}
                  </span>
                )}
              </label>
            )}
          </div>

          {/* Contratos (read-only informativo) */}
          <div className="mt-3 text-sm">
            <span className="opacity-80">
              {activeContracts.length === 0
                ? 'Sem contratos de shelf-life ativos.'
                : `${activeContracts.length} contrato(s) de shelf-life ativo(s).`}
            </span>
            {partyLinks.length > 0 && (
              <span className="ml-1">
                Geridos em:{' '}
                {partyLinks.map((p, i) => (
                  <span key={p.to}>
                    {i > 0 && ', '}
                    <Link className="text-blue-600 underline" to={p.to}>
                      {p.label}
                    </Link>
                  </span>
                ))}
                .
              </span>
            )}
            <div className="mt-1 text-xs opacity-70">
              A presença de contrato ativo implica controlo de validade
              (derivado no servidor).
            </div>
          </div>
        </div>

        {/* Imagens (WMS-owned, ADR-0046) */}
        <div className="mt-5">
          <h3 className="text-sm font-semibold opacity-80">Imagens</h3>
          <button
            className="btn subtle mt-2"
            onClick={() => setImgOpen(true)}
            disabled={!id}
          >
            Gerir imagens
          </button>
        </div>

        {(formError || otherFieldErrors.length > 0) && (
          <div className="mt-3 text-sm text-red-600">
            {formError && <div>{formError}</div>}
            {otherFieldErrors.map(([k, m]) => (
              <div key={k} className="text-xs">
                {k}: {m}
              </div>
            ))}
          </div>
        )}
      </div>

      {id && imgOpen && (
        <ProductImagesDialog
          product={{ id: Number(id), code: form.code ?? '', name1: form.name1 }}
          open
          onClose={() => setImgOpen(false)}
        />
      )}
    </div>
  )
}
